import React, { useState, useRef } from 'react';
import { Button, Input } from '../../../Common';
import './TwoFactorAuthForm.css';

type TwoFactorAuthFormProps = {
  title?: string;
  buttonText?: string;
  onSubmit?: (code: string) => Promise<void> | void;
  onBackToSignIn?: () => void;
  className?: string;
};

const TwoFactorAuthForm: React.FC<TwoFactorAuthFormProps> = ({
  title = 'Confirm Sign-in',
  buttonText = 'Confirm',
  onSubmit,
  onBackToSignIn,
  className = '',
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) value = value[0];
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    
    try {
      await onSubmit?.(fullCode);
    } catch (error) {
      console.error('Two-factor auth failed:', error);
      throw error;
    }
  };

  const isCodeComplete = code.every(digit => digit !== '');

  return (
    <div className={`two-factor-container ${className}`}>
      <div className="two-factor-title-section">
        <h1 className="two-factor-title">{title}</h1>
        <p className="two-factor-subtitle">Please enter the code we sent to your email</p>
      </div>
      
      <form onSubmit={handleSubmit} className="two-factor-form-fields">
        <div className="two-factor-code-inputs">
          {code.map((digit, index) => (
            <Input
              key={index}
              value={digit}
              onValueChange={(value) => handleChange(index, value)}
              onKeyDown={(e) => handleKeyDown(index, e as any)}
              inputRef={(el: any) => (inputRefs.current[index] = el)}
              slotProps={{
                htmlInput: {
                  maxLength: 1,
                  style: { textAlign: 'center' },
                } as React.InputHTMLAttributes<HTMLInputElement>
              }}
              sx={{
                flex: 1,
              }}
            />
          ))}
        </div>
        
        <Button
          type="submit"
          primary
          size="full"
          disabled={!isCodeComplete}
        >
          {buttonText}
        </Button>
      </form>

      {onBackToSignIn && (
        <Button
          type="button"
          onClick={onBackToSignIn}
          primary={false}
          bordered={false}
          size="full"
        >
          Back to Sign In
        </Button>
      )}
    </div>
  );
};

export default TwoFactorAuthForm;
